import { Pipe, PipeTransform } from '@angular/core';
import { mdHardBreaks } from '@shared/utils/markdown-export.utils';

/**
 * Vast 9-kleurenpalet voor de notitie-/reactie-opmaak: goed verdeelde
 * basistinten met een lichte dusk-invloed (gedempt, leesbaar op wit). De KEY
 * is het opslagcontract (`{color:key}…{/color}`) en de CSS-class
 * (`note-color-<key>`, gedefinieerd in styles/_global.scss); de hex dient
 * voor de editor-preview en de inline styles in e-mails
 * (FeedbackCommentMarkup.ToHtml — wijzigingen altijd samen doorvoeren).
 */
export const NOTE_COLOR_SWATCHES: readonly { readonly key: string; readonly hex: string }[] = [
  { key: 'red', hex: '#a85550' },
  { key: 'orange', hex: '#a8763d' },
  { key: 'olive', hex: '#8f8a3f' },
  { key: 'green', hex: '#4f8a5c' },
  { key: 'teal', hex: '#3f8a85' },
  { key: 'blue', hex: '#4f7aa8' },
  { key: 'violet', hex: '#6f5fa8' },
  { key: 'plum', hex: '#9a5585' },
  { key: 'dusk', hex: '#6b6675' },
];

const COLOR_HEX_BY_KEY = new Map(NOTE_COLOR_SWATCHES.map(swatch => [swatch.key, swatch.hex]));
const COLOR_KEY_BY_HEX = new Map(NOTE_COLOR_SWATCHES.map(swatch => [swatch.hex.toLowerCase(), swatch.key]));

export function noteColorHex(key: string): string | undefined {
  return COLOR_HEX_BY_KEY.get(key);
}

/** Hex of `rgb(r, g, b)` → palet-key; onbekende kleuren leveren undefined (opmaak valt dan weg). */
export function noteColorKeyForCss(cssColor: string | null | undefined): string | undefined {
  if (!cssColor) return undefined;
  const value = cssColor.trim().toLowerCase();
  const direct = COLOR_KEY_BY_HEX.get(value);
  if (direct) return direct;
  const rgb = /^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/.exec(value);
  if (!rgb) return undefined;
  const hex = `#${[rgb[1], rgb[2], rgb[3]]
    .map(part => Number(part).toString(16).padStart(2, '0'))
    .join('')}`;
  return COLOR_KEY_BY_HEX.get(hex);
}

/**
 * Sumiere notitie-/reactie-opmaak: `**vet**`, `_cursief_`, `++onderstreept++`,
 * `~~doorgehaald~~`, `^^superscript^^`, `{color:key}gekleurd{/color}`, regels
 * met `- `/`* ` (bullets) of `1. ` (nummers), en regeleinden. Alles wordt
 * eerst HTML-geëscaped; de uitvoer bevat uitsluitend
 * strong/em/u/s/sup/span[class]/ul/ol/li/br en overleeft daarmee ook Angular's
 * innerHTML-sanitizer (kleur via class, niet via style — de sanitizer stript
 * style-attributen). Spiegel van FeedbackCommentMarkup.ToHtml in de backend
 * (Egitura.Platform.Domain/Services/Email/FeedbackCommentMarkup.cs):
 * wijzigingen altijd samen doorvoeren.
 */
export function renderSupportMarkup(body: string | null | undefined): string {
  if (!body) return '';

  const lines = body.replace(/\r\n/g, '\n').split('\n');
  const html: string[] = [];
  const textBlock: string[] = [];
  let listTag: 'ul' | 'ol' | undefined;

  const flushText = (): void => {
    if (textBlock.length === 0) return;
    html.push(textBlock.join('<br>'));
    textBlock.length = 0;
  };

  const closeList = (): void => {
    if (!listTag) return;
    html.push(`</${listTag}>`);
    listTag = undefined;
  };

  for (const line of lines) {
    const bullet = /^\s*[-*]\s+(.*)$/.exec(line);
    const number = bullet ? undefined : /^\s*\d+\.\s+(.*)$/.exec(line);
    const itemTag = bullet ? 'ul' : number ? 'ol' : undefined;

    if (!itemTag) {
      closeList();
      textBlock.push(renderInline(line));
      continue;
    }

    flushText();
    if (listTag !== itemTag) {
      closeList();
      html.push(`<${itemTag}>`);
      listTag = itemTag;
    }
    html.push(`<li>${renderInline((bullet ?? number)![1])}</li>`);
  }

  closeList();
  flushText();
  return html.join('');
}

function renderInline(line: string): string {
  const escaped = escapeHtml(line);
  // Kleur eerst (omhult doorgaans de rest), cursief als laatste (losse `_` is
  // het meest botsingsgevoelig). Onbekende kleur-keys renderen als kale tekst.
  const withColor = escaped.replace(/\{color:([a-z]+)\}(.+?)\{\/color\}/g, (match, key: string, inner: string) =>
    COLOR_HEX_BY_KEY.has(key) ? `<span class="note-color-${key}">${inner}</span>` : inner);
  const withBold = withColor.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  const withUnderline = withBold.replace(/\+\+(.+?)\+\+/g, '<u>$1</u>');
  const withStrike = withUnderline.replace(/~~(.+?)~~/g, '<s>$1</s>');
  const withSup = withStrike.replace(/\^\^(.+?)\^\^/g, '<sup>$1</sup>');
  return withSup.replace(/_(.+?)_/g, '<em>$1</em>');
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** Non-breaking space (contenteditable strooit ermee); als charcode tegen editor-escaping-ellende. */
const NBSP = String.fromCharCode(0xa0);

/** Effectieve inline-opmaak van een stuk tekst, los van hoe de DOM die bereikt (tags of styles). */
interface InlineStyleState {
  readonly bold: boolean;
  readonly italic: boolean;
  readonly underline: boolean;
  readonly strike: boolean;
  readonly sup: boolean;
  readonly colorKey: string | undefined;
}

const PLAIN_STATE: InlineStyleState = { bold: false, italic: false, underline: false, strike: false, sup: false, colorKey: undefined };

interface InlineRun {
  readonly text: string;
  readonly state: InlineStyleState;
}

/**
 * Volgorde van buiten naar binnen bij het uitschrijven — spiegel van de
 * parse-volgorde in {@link renderSupportMarkup} (kleur eerst, cursief laatst),
 * zodat de round-trip exact stabiel is.
 */
const STYLE_ORDER: readonly (keyof InlineStyleState)[] = ['colorKey', 'bold', 'underline', 'strike', 'sup', 'italic'];

const STYLE_MARKERS: Partial<Record<keyof InlineStyleState, string>> = {
  bold: '**',
  underline: '++',
  strike: '~~',
  sup: '^^',
  italic: '_',
};

/**
 * Elementen die een eigen regel afdwingen. De editor zelf maakt alleen
 * div/p/br, de rest is voor geplakte inhoud (Word gebruikt <p>, webpagina's
 * koppen, tabellen enzovoort — elke cel of rij wordt hier een regel).
 */
const BLOCK_TAGS = new Set([
  'DIV', 'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE', 'PRE',
  'SECTION', 'ARTICLE', 'HEADER', 'FOOTER', 'MAIN', 'ASIDE',
  'FIGURE', 'FIGCAPTION', 'ADDRESS', 'TABLE', 'TR', 'TD', 'TH', 'DL', 'DT', 'DD',
]);

/**
 * Opmaak-effect van één element bovenop de ge-erfde toestand. Tags (b/strong,
 * i/em, u/ins, s/strike/del, sup, font[color], note-color-*-classes) én inline
 * styles tellen mee — execCommand wisselt daar vrolijk tussen, en zet opmaak
 * ook wel eens juist UIT via een style (font-weight: normal binnen een <b>).
 */
function deriveInlineState(element: HTMLElement, parent: InlineStyleState): InlineStyleState {
  let { bold, italic, underline, strike, sup, colorKey } = parent;

  switch (element.tagName) {
    // H1 t/m H6: koppen uit geplakte inhoud landen als vette regel.
    case 'B':
    case 'STRONG':
    case 'H1':
    case 'H2':
    case 'H3':
    case 'H4':
    case 'H5':
    case 'H6':
      bold = true;
      break;
    case 'I':
    case 'EM':
      italic = true;
      break;
    case 'U':
    case 'INS':
      underline = true;
      break;
    case 'S':
    case 'STRIKE':
    case 'DEL':
      strike = true;
      break;
    case 'SUP':
      sup = true;
      break;
    case 'FONT': {
      const key = noteColorKeyForCss(element.getAttribute('color'));
      if (key) colorKey = key;
      break;
    }
  }

  const classKey = /(?:^|\s)note-color-([a-z]+)(?:\s|$)/.exec(element.className ?? '')?.[1];
  if (classKey && noteColorHex(classKey)) colorKey = classKey;

  const style = element.style;
  if (style) {
    const weight = style.fontWeight;
    if (weight === 'bold' || weight === 'bolder' || Number(weight) >= 600) bold = true;
    else if (weight === 'normal' || weight === 'lighter' || (Number(weight) > 0 && Number(weight) < 600)) bold = false;

    if (style.fontStyle === 'italic' || style.fontStyle === 'oblique') italic = true;
    else if (style.fontStyle === 'normal') italic = false;

    const decoration = style.textDecorationLine || style.textDecoration;
    if (decoration) {
      if (decoration.includes('underline')) underline = true;
      if (decoration.includes('line-through')) strike = true;
      if (decoration.includes('none')) {
        underline = false;
        strike = false;
      }
    }

    if (style.verticalAlign === 'super') sup = true;
    else if (style.verticalAlign === 'baseline') sup = false;

    if (style.color) {
      const key = noteColorKeyForCss(style.color);
      if (key) colorKey = key;
    }
  }

  return { bold, italic, underline, strike, sup, colorKey };
}

/**
 * Schrijft een reeks runs uit als markup: per stijl-laag (STYLE_ORDER) worden
 * aaneengesloten runs met dezelfde waarde gegroepeerd en één keer omwikkeld.
 * Zo blijven markers minimaal en genest in vaste volgorde, en normaliseert
 * dubbel-toegepaste opmaak (sup-in-sup) vanzelf weg.
 */
function emitRuns(runs: readonly InlineRun[], level = 0): string {
  if (runs.length === 0) return '';
  if (level >= STYLE_ORDER.length) return runs.map(run => run.text).join('');

  const prop = STYLE_ORDER[level];
  let out = '';
  let index = 0;
  while (index < runs.length) {
    const value = runs[index].state[prop];
    let end = index;
    while (end < runs.length && runs[end].state[prop] === value) end++;
    const inner = emitRuns(runs.slice(index, end), level + 1);
    if (inner) {
      if (prop === 'colorKey') out += value ? `{color:${value}}${inner}{/color}` : inner;
      else out += value ? `${STYLE_MARKERS[prop]}${inner}${STYLE_MARKERS[prop]}` : inner;
    }
    index = end;
  }
  return out;
}

/**
 * Serialiseert de inhoud van de WYSIWYG-editor (contenteditable) terug naar de
 * markup-opslagvorm. Werkt op de EFFECTIEVE opmaak per stuk tekst (runs) in
 * plaats van de DOM-structuur te spiegelen: zo overleven ook Chrome's grillen
 * — vet over meerdere regels (`<b>a<br>b</b>` wordt per regel `**a**`/`**b**`),
 * style-spans, gesplitste tags en dubbel geneste sup. Alleen paletkleuren
 * blijven; andere kleuren vallen weg als kale tekst. Round-trip-contract met
 * {@link renderSupportMarkup}.
 */
export function htmlToSupportMarkup(root: HTMLElement): string {
  const lines: string[] = [];
  let currentRuns: InlineRun[] = [];

  const flushLine = (): void => {
    lines.push(emitRuns(currentRuns));
    currentRuns = [];
  };
  const lineHasContent = (): boolean => currentRuns.length > 0;

  const pushText = (node: Node, state: InlineStyleState, sink: InlineRun[]): void => {
    const text = (node.textContent ?? '').split(NBSP).join(' ');
    if (text) sink.push({ text, state });
  };

  /** Inline verzamelen binnen een lijst-item: regeleinden worden daar spaties. */
  const collectListItem = (node: Node, state: InlineStyleState, sink: InlineRun[]): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      pushText(node, state, sink);
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const element = node as HTMLElement;
    if (element.tagName === 'BR') {
      sink.push({ text: ' ', state });
      return;
    }
    const derived = deriveInlineState(element, state);
    Array.from(element.childNodes).forEach(child => collectListItem(child, derived, sink));
  };

  const walkBlock = (node: Node, state: InlineStyleState): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      pushText(node, state, currentRuns);
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const element = node as HTMLElement;

    if (element.tagName === 'BR') {
      flushLine();
      return;
    }
    if (element.tagName === 'UL' || element.tagName === 'OL') {
      if (lineHasContent()) flushLine();
      const ordered = element.tagName === 'OL';
      Array.from(element.children).forEach((item, index) => {
        if (item.tagName !== 'LI') return;
        const runs: InlineRun[] = [];
        Array.from(item.childNodes).forEach(child => collectListItem(child, state, runs));
        lines.push(ordered ? `${index + 1}. ${emitRuns(runs)}` : `- ${emitRuns(runs)}`);
      });
      return;
    }
    if (BLOCK_TAGS.has(element.tagName)) {
      if (lineHasContent()) flushLine();
      const derived = deriveInlineState(element, state);
      Array.from(element.childNodes).forEach(child => walkBlock(child, derived));
      if (lineHasContent()) flushLine();
      // Een leeg blok (alleen een <br> die al geflusht is) telt als lege regel;
      // een écht leeg blok ook.
      else if (element.childNodes.length === 0) lines.push('');
      return;
    }
    // Opmaak-element (b/i/u/s/sup/span/font/...): toestand afleiden en
    // doorlopen als blok, zodat een <br> erbínnen gewoon een regeleinde blijft.
    const derived = deriveInlineState(element, state);
    Array.from(element.childNodes).forEach(child => walkBlock(child, derived));
  };

  Array.from(root.childNodes).forEach(node => walkBlock(node, PLAIN_STATE));
  if (lineHasContent()) flushLine();

  // Afsluitende lege regels wegknippen (contenteditable laat er graag één achter).
  while (lines.length > 0 && lines[lines.length - 1].trim() === '') {
    lines.pop();
  }
  return lines.join('\n');
}

/**
 * Zet HTML van het klembord (Word, webpagina's, mails) om naar onze markup:
 * ondersteunde opmaak (vet, cursief, onderstreept, doorgehaald, superscript,
 * lijsten, exacte paletkleuren, koppen als vet) krijgt zijn equivalent, al de
 * rest valt weg als kale tekst. DOMParser is inert (geen scripts, niets wordt
 * aan de pagina gehangen); style-/script-blokken gaan er eerst uit zodat hun
 * tekstinhoud niet meeplakt.
 */
export function pastedHtmlToSupportMarkup(html: string): string {
  const parsed = new DOMParser().parseFromString(html, 'text/html');
  parsed.body.querySelectorAll('style, script, meta, link, title, noscript, template').forEach(element => element.remove());
  return htmlToSupportMarkup(parsed.body);
}

/**
 * Zet markup om naar Markdown voor de exports (momenten, spec
 * 2026-10-21-dagboek-momenten-export-design §1): vet, cursief en doorhalen zijn al
 * Markdown, onderstrepen en superscript worden `<u>`/`<sup>`, kleur valt weg (de tekst
 * blijft), lijsten blijven lijsten en regeleinden worden harde breaks. HTML in de tekst
 * zelf wordt geneutraliseerd.
 */
export function supportMarkupToMarkdown(body: string | null | undefined): string {
  if (!body) return '';
  const lines = body
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map(line => {
      const item = /^(\s*)([-*]|\d+\.)\s+(.*)$/.exec(line);
      const content = (item ? item[3] : line)
        .replace(/</g, '&lt;')
        .replace(/\{color:[a-z]+\}(.+?)\{\/color\}/g, '$1')
        .replace(/\+\+(.+?)\+\+/g, '<u>$1</u>')
        .replace(/\^\^(.+?)\^\^/g, '<sup>$1</sup>');
      if (item) return `${item[1]}${item[2] === '*' ? '-' : item[2]} ${content}`;
      return content.replace(/^(\s*)(#|>)/, '$1\\$2');
    });
  return mdHardBreaks(lines.join('\n')).trim();
}

/**
 * Zet markup om naar kale tekst: alle inline-opmaak en kleuren verdwijnen,
 * lijstitems worden gewone regels. Voor de "opmaak wissen"-actie in de editor.
 * Loopt via de canonieke render zodat markers en tekst exact hetzelfde
 * geïnterpreteerd worden als in de weergave.
 */
export function stripSupportMarkup(body: string | null | undefined): string {
  if (!body) return '';
  const root = document.createElement('div');
  root.innerHTML = renderSupportMarkup(body);

  const lines: string[] = [];
  let current = '';
  let lineOpen = false;
  const flush = (): void => {
    lines.push(current);
    current = '';
    lineOpen = false;
  };
  const walk = (node: Node): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      current += node.textContent ?? '';
      lineOpen = true;
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const element = node as HTMLElement;
    if (element.tagName === 'BR') {
      flush();
      return;
    }
    if (element.tagName === 'UL' || element.tagName === 'OL') {
      if (lineOpen) flush();
      Array.from(element.children).forEach(item => {
        if (item.tagName === 'LI') lines.push(item.textContent ?? '');
      });
      return;
    }
    Array.from(element.childNodes).forEach(walk);
  };

  Array.from(root.childNodes).forEach(walk);
  if (lineOpen) flush();
  return lines.join('\n');
}

/** Rendert reactietekst naar veilige HTML voor [innerHTML]; zie renderSupportMarkup. */
@Pipe({ name: 'supportMarkup' })
export class SupportMarkupPipe implements PipeTransform {
  transform(body: string | null | undefined): string {
    return renderSupportMarkup(body);
  }
}
